import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import api from '../lib/api'

const useAuthStore = create(
  persist(
    (set, get) => ({
      // State
      user: null,
      token: null,
      isAuthenticated: false,
      isLoading: false,
      
      // Actions
      login: async (email, password) => {
        set({ isLoading: true })
        try {
          const response = await api.post('/auth/login', { email, password })
          const data = response.data
          
          console.log('📥 Login response:', data)
          
          // Handle response formats - NO HARDCODED FALLBACKS
          let token, user
          
          if (data.access_token) {
            token = data.access_token
            
            // Format 1: Full user object in response (from auth.py)
            if (data.user && data.user.id) {
              user = data.user
              console.log('✅ Using Format 1: Full user object')
            }
            // Format 2: Simple user data (from main_complete.py) 
            else if (data.user_id) {
              user = {
                id: data.user_id,
                email: data.email || email,
                full_name: data.full_name || email,
                user_type: data.user_type || 'free',
                max_journeys: data.max_journeys || 3,
                journeys_used: data.journeys_used || 0
              }
              console.log('✅ Using Format 2: Simple user data')
            }
            else {
              // NO FALLBACK - Force proper authentication
              throw new Error('Invalid login response: missing user ID. Backend must provide user.id or user_id')
            }
          } else {
            throw new Error('No access token in response')
          }
          
          // Validate user ID exists and is not generic
          if (!user.id || user.id === 'current_user' || user.id === 'authenticated_user') {
            throw new Error(`Invalid user ID received: ${user.id}. Each user must have a unique ID.`)
          }
          
          // Set token in localStorage and axios headers
          localStorage.setItem('token', token)
          api.defaults.headers.common['Authorization'] = `Bearer ${token}`
          
          set({
            user: user,
            token: token,
            isAuthenticated: true,
            isLoading: false
          })
          
          console.log('✅ Login successful for user:', user.id)
          return { success: true }
        } catch (error) {
          set({ isLoading: false })
          console.error('❌ Login error:', error)
          console.error('Response data:', error.response?.data)
          
          return { 
            success: false, 
            error: error.response?.data?.detail || error.message || 'Login failed' 
          }
        }
      },
      
      register: async (userData) => {
        set({ isLoading: true })
        try {
          const response = await api.post('/auth/register', userData)
          const data = response.data
          
          console.log('📥 Registration response:', data)
          
          // Handle registration response format - NO HARDCODED FALLBACKS
          let token, user
          
          if (data.access_token) {
            token = data.access_token
            
            // Format 1: Full user object in response  
            if (data.user && data.user.id) {
              user = data.user
              console.log('✅ Registration Format 1: Full user object')
            }
            // Format 2: Simple user data
            else if (data.user_id) {
              user = {
                id: data.user_id,
                email: data.email || userData.email,
                full_name: data.full_name || userData.full_name,
                user_type: data.user_type || 'free',
                max_journeys: data.max_journeys || 3,
                journeys_used: data.journeys_used || 0
              }
              console.log('✅ Registration Format 2: Simple user data')
            }
            else {
              // NO FALLBACK - Force proper response format
              throw new Error('Invalid registration response: missing user ID. Backend must provide user.id or user_id')
            }
          } else {
            // If no token returned, try logging in after registration
            console.log('🔄 No token in registration, attempting login...')
            return await get().login(userData.email, userData.password)
          }
          
          // Validate user ID
          if (!user.id || user.id === 'current_user' || user.id === 'new_user') {
            throw new Error(`Invalid user ID received: ${user.id}. Each user must have a unique ID.`)
          }
          
          // Set token in localStorage and axios headers
          localStorage.setItem('token', token)
          api.defaults.headers.common['Authorization'] = `Bearer ${token}`
          
          set({
            user: user,
            token: token,
            isAuthenticated: true,
            isLoading: false
          })
          
          console.log('✅ Registration successful for user:', user.id)
          return { success: true }
        } catch (error) {
          set({ isLoading: false })
          console.error('❌ Registration error:', error)
          console.error('Response data:', error.response?.data)
          
          return { 
            success: false, 
            error: error.response?.data?.detail || error.message || 'Registration failed' 
          }
        }
      },
      
      logout: async () => {
        const currentUser = get().user
        console.log('🚪 Logging out user:', currentUser?.id)
        
        try {
          // Call backend to clear user agent and session
          await api.post('/auth/logout').catch(error => {
            console.warn('⚠️ Logout API call failed:', error.message)
            // Continue with frontend logout even if backend fails
          })
        } catch (error) {
          console.warn('⚠️ Logout cleanup failed:', error.message)
        }
        
        // Clear frontend state
        localStorage.removeItem('token')
        delete api.defaults.headers.common['Authorization']
        set({
          user: null,
          token: null,
          isAuthenticated: false,
          isLoading: false
        })
        
        console.log('✅ User logged out successfully')
      },
      
      checkAuth: async () => {
        const token = localStorage.getItem('token')
        if (!token) {
          set({ isAuthenticated: false })
          return
        }
        
        try {
          api.defaults.headers.common['Authorization'] = `Bearer ${token}`
          
          // Try to get user data from backend
          const response = await api.get('/auth/me')
          const userData = response.data
          
          console.log('📥 Auth check response:', userData)
          
          // STRICT VALIDATION - No generic users allowed
          if (userData.id && userData.id !== 'authenticated_user' && userData.id !== 'current_user') {
            // Valid user object with unique ID
            set({
              user: userData,
              token,
              isAuthenticated: true
            })
            console.log('✅ Auth check successful for user:', userData.id)
          } else {
            // Invalid or generic user - force logout
            console.error('❌ Invalid user session - generic or missing user ID:', userData.id)
            throw new Error('Invalid user session')
          }
        } catch (error) {
          console.error('❌ Auth check error:', error)
          // Token is invalid or session expired - clear everything
          localStorage.removeItem('token')
          delete api.defaults.headers.common['Authorization']
          set({
            user: null,
            token: null,
            isAuthenticated: false
          })
        }
      },
      
      updateProfile: async (profileData) => {
        set({ isLoading: true })
        try {
          // Get updated user data from backend
          const response = await api.get('/auth/me')
          const userData = response.data
          
          // Validate response
          if (userData.id && userData.id !== 'authenticated_user') {
            set({
              user: userData,
              isLoading: false
            })
            console.log('✅ Profile updated for user:', userData.id)
            return { success: true }
          } else {
            throw new Error('Invalid user data received')
          }
        } catch (error) {
          set({ isLoading: false })
          console.error('❌ Profile update error:', error)
          return { 
            success: false, 
            error: error.response?.data?.detail || 'Profile update failed' 
          }
        }
      }
    }),
    {
      name: 'samvadic-auth',
      partialize: (state) => ({ 
        user: state.user, 
        token: state.token, 
        isAuthenticated: state.isAuthenticated 
      }),
    }
  )
)

export { useAuthStore }